/* eslint-disable @typescript-eslint/no-explicit-any */
import { TemplateResult, html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafModal } from '@cigna/dcws_leaf-web-components/components/modal/modal.component.js';
import { LeafSelect } from '@cigna/dcws_leaf-web-components/components/select/select.component.js';
import { LeafSelectOption } from '@cigna/dcws_leaf-web-components/components/select-option/select-option.component.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { LeafValidationMessage } from '@cigna/dcws_leaf-web-components/components/validation-message/validation-message.component.js';
import { LeafList } from '@cigna/dcws_leaf-web-components/components/list/list.component.js';
import { LeafTab } from '@cigna/dcws_leaf-web-components/components/tabs/tabs.js';
import { LeafTabSection } from '@cigna/dcws_leaf-web-components/components/tabs/tab-section.js';
import { LeafTabItem } from '@cigna/dcws_leaf-web-components/components/tabs/tab-item.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import styles from './medicare-lookup-tool.scss?inline';
import { PDFList, PDFListData } from './interfaces.js';

export class CHCMedicareLookupTool extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-input': LeafInput,
    'leaf-label': LeafLabel,
    'leaf-modal': LeafModal,
    'leaf-select': LeafSelect,
    'leaf-select-option': LeafSelectOption,
    'leaf-spinner': LeafSpinner,
    'leaf-validation-message': LeafValidationMessage,
    'leaf-tab': LeafTab,
    'leaf-tab-section': LeafTabSection,
    'leaf-tab-item': LeafTabItem,
    'leaf-list': LeafList,
  };

  @property({ type: String, reflect: true })
  override ns: string = 'medicare-eoc-lookup-tool';

  private medicareLookupToolData?: PDFListData;

  @state()
  stateList: TemplateResult<1>[] = [];

  @state()
  tabList: TemplateResult<1>[] = [];

  @state()
  tabContentList: TemplateResult<1>[] = [];

  @state()
  selectedState!: string | null;

  @property({ type: Array })
  years: string[] = [];

  override onNamespacesLoaded = () => {
    this.medicareLookupToolData = this.i18n?.getLanguageData(this.ns) as PDFListData;
  };

  groupByKey(array: any[] | undefined, key: string | number) {
    return array?.reduce((hash: { [x: string]: any }, obj: { [x: string]: string | number }) => {
      if (obj[key] === undefined) return hash;
      return Object.assign(hash, { [obj[key]]: (hash[obj[key]] || []).concat(obj) });
    }, {});
  }

  renderPDFLists(year: number) {
    const stateContent: TemplateResult<1>[] = [];
    let filteredAndGrouped: PDFList[] | undefined = this.medicareLookupToolData?.data.filter(
      (itm: { Year: number }) => {
        return itm.Year == year;
      },
    );
    filteredAndGrouped = this.groupByKey(filteredAndGrouped, 'State');

    if (filteredAndGrouped) {
      let property: keyof typeof filteredAndGrouped;
      for (property in filteredAndGrouped) {
        stateContent.push(
          html`<leaf-list
            class="leaf-u-margin-top-24"
            background="transparent"
            layout="vertical"
            unstyled
            type="unordered"
            variant="default"
            data-state="${property}"
            data-year="${year}"
            hidden
          >
            ${Object.values(filteredAndGrouped[property]).map(
              (group: { State: string; Plan_Name: string; PDF_URL: string; Counties: string; Eligibility: string }) =>
                html`<li>
                  <a href="${group.PDF_URL}" target="_blank">${group.Plan_Name} [PDF]</a><br />${unsafeHTML(
                    this.medicareLookupToolData?.misc.countiesText,
                  )}${group.Counties}${group.Eligibility ? html`<p>${group.Eligibility}</p>` : ''}
                </li>`,
            )}
          </leaf-list> `,
        );
      }
    }
    return html`${stateContent} `;
  }

  renderStateDD(year: number | string) {
    this.stateList = [];
    let filteredAndGrouped = this.medicareLookupToolData?.data.filter((itm: { Year: number }) => {
      return itm.Year == year;
    });
    filteredAndGrouped = this.groupByKey(filteredAndGrouped, 'State');
    for (const state in filteredAndGrouped) {
      this.stateList.push(html`<leaf-select-option value="${state}"> ${state} </leaf-select-option>`);
    }
    return html`${this.stateList}`;
  }

  renderResults = (event: SubmitEvent) => {
    event.preventDefault();
    const activeTabSection =
      this.years.length > 1
        ? this.shadowRoot?.querySelector(`leaf-tab-section[active]`)
        : this.shadowRoot?.querySelector(`.current-year-section`);
    activeTabSection?.querySelectorAll('select').forEach((el) => {
      if (el.getAttribute('value') === null) {
        this.selectedState = null;
      } else {
        this.selectedState = el.getAttribute('value');
      }
      activeTabSection?.querySelectorAll('leaf-list').forEach((list) => {
        if (this.selectedState === list.getAttribute('data-state')) {
          list.removeAttribute('hidden');
        } else list.setAttribute('hidden', '');
      });
    });
  };

  renderChildContent(year: string) {
    return html` ${this.renderForm(year)}
      <h4 class="leaf-typography-label-large" ?hidden=${!this.selectedState}>
        ${year} ${this.medicareLookupToolData?.misc.heading} ${this.selectedState}
      </h4>
      <div class="hide-show-select-container">${this.renderPDFLists(parseInt(year))}</div>
      <p class="update-message">
        ${year === this.medicareLookupToolData?.misc.nextYear
          ? this.medicareLookupToolData?.misc.updateMessageNextYear
          : this.medicareLookupToolData?.misc.updateMessageCurrentYear}
      </p>`;
  }

  renderTabs() {
    this.tabList = [];
    this.tabContentList = [];
    this.years.forEach((year, index) => {
      if (this.years.length >= 2) {
        this.tabList.push(
          html`<leaf-tab-item
            slot="tab"
            scroll-target
            class="focus-visible"
            aria-selected=${index === 0 ? true : false}
            label="${year as string}"
            ?active=${index === 0 ? true : false}
            size="default"
            @click="${this.toggleTabs}"
            >${year}</leaf-tab-item
          >`,
        );
        this.tabContentList.push(
          html` <leaf-tab-section
            slot="tab-section"
            label="${year as string}"
            has-hr="true"
            ?active=${index === 0}
            aria-hidden=${index === 0 ? false : true}
          >
            ${this.renderChildContent(year)}
          </leaf-tab-section>`,
        );
      } else {
        this.tabList.push(html` <div class="leaf-u-margin-top-none leaf-typography-label-large">${year}</div> `);
        this.tabContentList.push(html`<div class="current-year-section">${this.renderChildContent(year)}</div>`);
      }
    });
    return html`${this.tabList}${this.tabContentList}`;
  }

  renderForm(year: number | string) {
    return html`
      <form type="external" pdf-hide-show-select @submit=${this.renderResults}>
        <div>
          <leaf-label for="pdf-state-select">${this.medicareLookupToolData?.misc.labelText}</leaf-label>
          <leaf-select
            label="${ifDefined(this.medicareLookupToolData?.misc.labelText)}"
            name="state_lookup"
            required
            id="pdf-state-select"
          >
            <leaf-select-option value="" selected="true" disabled="disabled"
              >${this.medicareLookupToolData?.misc.labelText}</leaf-select-option
            >
            ${this.renderStateDD(year)}
          </leaf-select>
        </div>
        <leaf-button type="submit">${this.medicareLookupToolData?.misc.buttonText} </leaf-button>
      </form>
    `;
  }

  getYears() {
    this.years = this?.getAttribute('years')?.split(', ') || [];
  }

  toggleTabs() {
    const activeTab = this.shadowRoot?.querySelector(`leaf-tab-section[active="true"]`);
    activeTab?.querySelectorAll('select').forEach((el) => {
      if (el.getAttribute('value') === null) {
        this.selectedState = null;
      } else {
        this.selectedState = el.getAttribute('value');
      }
    });
  }

  override render(): TemplateResult {
    const loadingOrError = this.renderLoadingOrError();
    if (loadingOrError) {
      return loadingOrError;
    }
    this.getYears();
    return html`
      <span class="leaf-typography-label-large">${this.medicareLookupToolData?.misc.tabText}</span>
      ${this.years.length >= 2
        ? html`<leaf-tab mobile-variant="scroll" tab-align="left"> ${this.renderTabs()} </leaf-tab>`
        : this.renderTabs()}
    `;
  }
}
